import type { GanttIndicadores } from "@/shared/components/gantt-carga/gantt-carga.constants";
import { useEffect, useMemo, useState } from "react";
import type { MouseEvent } from "react";

import { formatMonthRangeLabel, resolveListEmptyKind } from "@/ds";
import type { FilterValue } from "@/ds";
import { getPersistedUser } from "@/modules/auth";
import { buildTimeline, calcularConflitos, todayIso } from "@/modules/carga-trabalho";
import { describeReportFilters, formatExportedLabel } from "@/modules/export";
import {
  agruparConflitos,
  BUSCA_MAX_LENGTH,
  buildCargaFornecedorFilterColumns,
  countCargaFornecedorLinhas,
  exportAlocacoesFornecedorPorObra,
  exportAlocacoesPorFornecedor,
  filtraFornecedorObraCancelada,
  hasCargaFornecedorFilters,
  isObraCancelada,
  isConflitoEntreObras,
  obrasParaProfissionais,
  periodoToRange,
  toGanttIndicators,
  toGanttObrasData,
  toGanttProfissionaisData,
  useCargaDeTrabalho as useCargaFornecedores,
} from "@/modules/recursos";
import type {
  AgrupamentoCarga,
  CargaFornecedorDados,
  ConflitoFornecedor,
  ObraEscopoMacro,
} from "@/modules/recursos";
import {
  aplicarCenarioFornecedores,
  aplicarCenarioObrasFornecedor,
  cargaDeFornecedores,
  conflitosFornecedores,
  conflitosObrasFornecedor,
  novosConflitos,
} from "@/modules/recursos/simulacao";
import type { AlteracaoSimulada, CenarioSimulacao } from "@/modules/recursos/simulacao";

import {
  AGRUPAMENTO_OPTIONS,
  DEFAULT_AGRUPAMENTO,
  DEFAULT_INDICADORES,
  INDICADORES_OBRA_E_CANCELADA,
  CARGA_FORNECEDOR_SAVED_FILTERS_SCREEN,
  CARGA_FORNECEDOR_SEARCH_PLACEHOLDER,
} from "../carga-de-trabalho.constants";
import { chipsDosFiltros, conteudoDoVazio, VAZIO_FILTRO } from "../carga-de-trabalho.utils";
import type { ConflictDetail } from "../components/conflict-detail-dialog";
import {
  IGNORADO_SUCCESS_MESSAGE,
  INDICADORES_ATUALIZADOS_MESSAGE,
  REMANEJADO_SUCCESS_MESSAGE,
} from "../components/resolver-conflito-fornecedor";
import type {
  ResolucaoFornecedorSimulada,
  ResolverConflitoFornecedorOutcome,
} from "../components/resolver-conflito-fornecedor";
import { buildFornecedorObraRows } from "../carga-fornecedor-gantt-rows";
import type { ObraAlocacaoRow } from "../carga-fornecedor-gantt-rows";
import { focoDaVisaoPorObras } from "@/shared/components/gantt-carga/gantt-obras-rows";
import { buildProfissionalRows } from "@/shared/components/gantt-carga/gantt-rows";
import type { GanttRowModel } from "@/shared/components/gantt-carga/gantt-rows";
import { useCargaExportacao } from "../hooks/use-carga-exportacao";
import { useRotulosDasObras } from "../hooks/use-rotulos-das-obras";
import { useCargaFiltrosSalvos } from "../hooks/use-carga-filtros-salvos";
import { usePeriodoDaAba } from "../hooks/use-carga-periodo";
import {
  CHIP_TEST_ID_PREFIX,
  ESTADO_IDS,
  GROUPING_HINT,
  LEGEND,
  LEGEND_TEST_ID,
  TOOLBAR_IDS,
  VAZIO,
  VAZIO_BUSCA,
} from "./carga-fornecedor.constants";
import type { CargaFornecedorProps, CargaFornecedorViewModel } from "./types-carga-fornecedor";

type LinhaEmConflito = Pick<GanttRowModel, "profissional" | "conflitos">;

/** Carga de Trabalho: perspectiva Fornecedor (US-1774), agrupada por Fornecedor ou por Obra. */
export function useCargaFornecedor({
  periodoCompartilhado,
  onPerspectivaChange,
  onNotify,
  cenario = null,
  onEntrarSimulacao,
  onAbrirHistorico,
  onIncluirNaSimulacao,
  onCargaAtual,
}: CargaFornecedorProps): CargaFornecedorViewModel {
  const simulando = cenario !== null;
  const [agrupamento, setAgrupamento] = useState<AgrupamentoCarga>(DEFAULT_AGRUPAMENTO);
  const { periodo: periodoMes, escolherPeriodo } = usePeriodoDaAba(periodoCompartilhado);
  const periodo = useMemo(() => ({ start: periodoMes.from, end: periodoMes.to }), [periodoMes]);
  const [search, setSearch] = useState("");
  const [filterValue, setFilterValue] = useState<FilterValue>({});
  const [indicators, setIndicators] = useState<GanttIndicadores>(DEFAULT_INDICADORES);
  const filtrosSalvos = useCargaFiltrosSalvos(
    CARGA_FORNECEDOR_SAVED_FILTERS_SCREEN,
    aplicarFiltros,
  );
  const [indicatorsOpen, setIndicatorsOpen] = useState(false);
  const [conflitoSelecionado, setConflitoSelecionado] = useState<ConflictDetail | null>(null);
  const [pontoDoConflito, setPontoDoConflito] = useState({ x: 0, y: 0 });
  const [resolverRow, setResolverRow] = useState<LinhaEmConflito | null>(null);
  // Conflitos ignorados nesta sessão seguem sinalizados mesmo antes da recarga trazê-los.
  const [ignoradosLocais, setIgnoradosLocais] = useState<{ alocacaoIds: string[] }[]>([]);

  const { status, dados, brutos, filtrados, marcas, errorMessage, reload } = useCargaFornecedores({
    agrupamento,
    periodo,
    search,
    filters: filterValue,
    cenario,
  });

  useEffect(() => {
    if (!onCargaAtual) return;
    onCargaAtual(brutos ? cargaDeFornecedores(brutos) : null);
  }, [brutos, onCargaAtual]);

  // As opções vêm dos dados sem filtro; a filtragem do painel é feita sobre eles, no cliente.
  const filterColumns = useMemo(() => buildCargaFornecedorFilterColumns(dados), [dados]);
  const rotulosDasObras = useRotulosDasObras(filterColumns);

  const today = useMemo(() => todayIso(), []);
  const ganttIndicators = useMemo(() => toGanttIndicators(indicators), [indicators]);
  const intervalo = useMemo(() => {
    const { dataInicio, dataFim } = periodoToRange(periodo);
    return { inicio: dataInicio, fim: dataFim };
  }, [periodo]);

  // Os dados viram a entrada do Gantt de Profissionais (fornecedor no lugar do profissional).
  const ganttData = useMemo(() => {
    if (!filtrados) return null;
    return filtrados.agrupamento === "obra"
      ? toGanttObrasData(filtrados, intervalo)
      : toGanttProfissionaisData(filtrados, intervalo);
  }, [filtrados, intervalo]);

  // A escala é o período escolhido: o cabeçalho mostra só os meses dele (as barras além das pontas são cortadas).
  // Por Fornecedor abre no início do período; por Obra, na 1ª alocação visível dele.
  const foco = useMemo(() => {
    if (filtrados?.agrupamento !== "obra") return intervalo.inicio;
    const alocacoes = filtrados.obras.flatMap((obra) =>
      obra.alocacoes.map(({ inicio, fim }) => ({
        inicio,
        fim,
        obraCancelada: isObraCancelada(obra.situacao),
      })),
    );
    return focoDaVisaoPorObras(alocacoes, intervalo, indicators.cancelada);
  }, [filtrados, intervalo, indicators.cancelada]);
  const timeline = useMemo(() => buildTimeline(intervalo, foco), [intervalo, foco]);

  // Fornecedor: o builder de Profissionais; Obras: uma linha por alocação (Obra | Escopo | Fornecedor).
  const fornecedorRows = useMemo(
    () =>
      status === "success" && ganttData && "profissionais" in ganttData
        ? buildProfissionalRows(
            ganttData,
            timeline,
            ganttIndicators,
            today,
            ignoradosLocais,
            isConflitoEntreObras,
          )
        : [],
    [status, ganttData, timeline, ganttIndicators, today, ignoradosLocais],
  );
  const obraRows = useMemo(
    () =>
      status === "success" && filtrados?.agrupamento === "obra"
        ? buildFornecedorObraRows(filtrados.obras, timeline, ganttIndicators, today)
        : [],
    [status, filtrados, timeline, ganttIndicators, today],
  );

  // Carga por fornecedor: base do "Gera conflito" dos candidatos; na visão por Obras, reagrupa.
  const cargaGantt = useMemo(() => {
    if (ganttData && "profissionais" in ganttData) return ganttData;
    if (filtrados?.agrupamento !== "obra") return null;
    return obrasParaProfissionais(filtrados, intervalo);
  }, [ganttData, filtrados, intervalo]);

  function resolverConflitoDaObra(row: ObraAlocacaoRow) {
    const profissional = cargaGantt?.profissionais.find((item) => item.id === row.fornecedorId);
    if (!profissional) return;
    const conflitos = calcularConflitos(profissional.alocacoes).filter(isConflitoEntreObras);
    if (conflitos.length > 0) setResolverRow({ profissional, conflitos });
  }

  // Conflito = alocações ligadas por sobreposição em obras distintas (N obras, sem limite).
  const resolverConflitos = useMemo<ConflitoFornecedor[]>(
    () => (resolverRow ? agruparConflitos(resolverRow.conflitos) : []),
    [resolverRow],
  );
  const escoposPorAlocacao = useMemo(() => {
    const result: Record<string, ObraEscopoMacro[] | undefined> = {};
    if (!resolverRow || !filtrados) return result;
    if (filtrados.agrupamento === "obra") {
      for (const obra of filtrados.obras) {
        for (const alocacao of obra.alocacoes) result[alocacao.id] = obra.escoposMacro ?? [];
      }
      return result;
    }
    const origem = filtrados.fornecedores.find(
      (item) => (item.id || item.nome) === resolverRow.profissional.id,
    );
    for (const alocacao of origem?.alocacoes ?? []) {
      result[alocacao.id] = alocacao.obra.escoposMacro ?? [];
    }
    return result;
  }, [resolverRow, filtrados]);

  // Na simulação, sem como incluir no cenário, não há resolução (ela gravaria).
  const podeResolver = !simulando || Boolean(onIncluirNaSimulacao);
  const itemCount = countCargaFornecedorLinhas(filtrados);
  const isEmpty = status === "success" && itemCount === 0;

  const hasSearch = search.trim() !== "";
  const hasFilters = hasCargaFornecedorFilters(filterValue);
  const emptyKind = resolveListEmptyKind(hasSearch, hasFilters);
  const periodoLabel = formatMonthRangeLabel(periodoMes);
  const printTitle =
    agrupamento === "fornecedor"
      ? "Carga de Trabalho — Fornecedores"
      : "Carga de Trabalho — Obras (fornecedores)";
  // O PDF imprime a tela, então traz também os filtros do painel aplicados.
  const reportFilters = describeReportFilters(
    search,
    filterColumns,
    filterValue,
    { periodo: periodoLabel },
    rotulosDasObras,
  );

  // Excel/CSV vêm do serviço, que só conhece busca e período: o painel de filtros não vai no arquivo.
  const { exportView, exportar } = useCargaExportacao({
    fileName: "carga-fornecedor",
    isEmpty,
    loadFile: (format) => {
      const exportFn =
        agrupamento === "fornecedor"
          ? exportAlocacoesPorFornecedor
          : exportAlocacoesFornecedorPorObra;
      return exportFn({
        dataInicio: intervalo.inicio,
        dataFim: intervalo.fim,
        busca: search,
        format,
        filtros: describeReportFilters(search, [], {}, { periodo: periodoLabel }),
      });
    },
    auditoria: {
      recurso:
        agrupamento === "fornecedor" ? "alocacoes/fornecedores" : "alocacoes/obras/fornecedores",
      quantidade: itemCount,
      inicio: intervalo.inicio,
      fim: intervalo.fim,
      busca: search,
    },
    printTitle,
  });

  // Cada indicador desligado some da legenda: a Cancelada só aparece com o indicador ligado.
  const legendItems = LEGEND.filter((item) => indicators[item.key as keyof GanttIndicadores]);

  function conflitoResolvido(outcome: ResolverConflitoFornecedorOutcome) {
    setResolverRow(null);
    if (outcome.tipo === "simulado") {
      // O cenário já recebeu a decisão (e o toast, em `incluirNaSimulacao`): nada foi gravado.
      return;
    }
    if (outcome.tipo === "ignorado") {
      setIgnoradosLocais((current) => [...current, { alocacaoIds: outcome.alocacaoIds }]);
    }
    onNotify?.({
      severity: "success",
      summary: "Sucesso",
      detail: outcome.tipo === "remanejado" ? REMANEJADO_SUCCESS_MESSAGE : IGNORADO_SUCCESS_MESSAGE,
    });
    reload();
  }

  /** Resolver Conflito na simulação (HU 2224): remanejar e ignorar viram alterações do cenário. */
  function resolucaoSimulada(fornecedor: {
    id: string;
    nome: string;
  }): ResolucaoFornecedorSimulada | undefined {
    if (!cenario || !onIncluirNaSimulacao || !dados) return undefined;
    const incluir = onIncluirNaSimulacao;
    const atual = dados;
    const codigo =
      (atual.agrupamento === "fornecedor"
        ? atual.fornecedores.find((item) => (item.id || item.nome) === fornecedor.id)?.codigo
        : atual.obras
            .flatMap((obra) => obra.alocacoes)
            .find(({ fornecedor: item }) => (item.id || item.nome) === fornecedor.id)?.fornecedor
            .codigo) ?? "";
    const recurso = { id: fornecedor.id, codigo, nome: fornecedor.nome };
    // A carga já tem o cenário: o conflito novo é o que esta alteração acrescenta a ela.
    const conflitosAbertos = (dadosAtuais: CargaFornecedorDados, alteracao?: AlteracaoSimulada) => {
      const unica: CenarioSimulacao = { alteracoes: alteracao ? [alteracao] : [] };
      return dadosAtuais.agrupamento === "fornecedor"
        ? conflitosFornecedores(aplicarCenarioFornecedores(dadosAtuais.fornecedores, unica).dados)
        : conflitosObrasFornecedor(aplicarCenarioObrasFornecedor(dadosAtuais.obras, unica).dados);
    };
    const comConflitos = (alteracao: AlteracaoSimulada) =>
      incluir({
        alteracao,
        conflitosNovos: novosConflitos(conflitosAbertos(atual), conflitosAbertos(atual, alteracao)),
      });

    return {
      remanejar: ({ alocacao, substituto, escopo, justificativa }) =>
        comConflitos({
          acao: "remanejar-fornecedor",
          ...(escopo ? { escopoMacro: escopo } : {}),
          obra: alocacao.obra,
          alocacaoId: alocacao.id,
          periodo: { inicio: alocacao.inicio, fim: alocacao.fim },
          anterior: recurso,
          novo: { id: substituto.id, codigo: substituto.codigo, nome: substituto.nome },
          justificativa,
        }),
      ignorar: ({ conflito, justificativa }) =>
        comConflitos({
          acao: "ignorar-conflito-fornecedor",
          fornecedor: recurso,
          obras: conflito.alocacoes.map((alocacao) => alocacao.obra),
          alocacaoIds: conflito.alocacoes.map((alocacao) => alocacao.id),
          conflito: { inicio: conflito.inicio, fim: conflito.fim },
          justificativa,
        }),
    };
  }

  /** Filtrar "Obra cancelada = Sim" liga o indicador Cancelada, senão a linha ficaria vazia. */
  function aplicarFiltros(next: FilterValue) {
    setFilterValue(next);
    if (filtraFornecedorObraCancelada(next)) {
      setIndicators((current) => ({ ...current, cancelada: true }));
    }
  }

  function limparTudo() {
    setSearch("");
    setFilterValue({});
  }

  function removerChip(key: string) {
    setFilterValue((current) => ({ ...current, [key]: [] }));
  }

  /** Pelo menos um indicador precisa continuar ativo: o modal do DS já barra, e aqui a regra fica garantida. */
  function salvarIndicadores(next: GanttIndicadores) {
    setIndicatorsOpen(false);
    if (!next.obra && !next.cancelada) return;
    setIndicators(next);
    onNotify?.({
      severity: "success",
      summary: "Sucesso",
      detail: INDICADORES_ATUALIZADOS_MESSAGE,
    });
  }

  function abrirDetalheDoConflito(conflicts: ConflictDetail, event: MouseEvent) {
    setConflitoSelecionado(conflicts);
    setPontoDoConflito({ x: event.clientX + 12, y: event.clientY + 12 });
  }

  const mostrarGantt = status === "success" && !isEmpty;
  const comAcoes = {
    marcas,
    timeline,
    today,
    onConflictHover: abrirDetalheDoConflito,
    onConflictLeave: () => setConflitoSelecionado(null),
  };

  return {
    estado: {
      status: status === "idle" ? "loading" : status,
      errorMessage,
      onRetry: reload,
      isEmpty,
      emptyKind,
      emptyContent: conteudoDoVazio(emptyKind, {
        vazio: VAZIO[agrupamento],
        busca: VAZIO_BUSCA[agrupamento],
        filtro: VAZIO_FILTRO,
      }),
      onClearEmpty: emptyKind === "sem-resultados-busca" ? () => setSearch("") : limparTudo,
      ids: ESTADO_IDS,
    },
    capaImpressao: {
      title: printTitle,
      generatedLabel: formatExportedLabel(getPersistedUser()?.name),
      filters: reportFilters,
      simulando,
    },
    legendaImpressao: { items: legendItems, simulando, forPrint: true, simuladoPreenchido: true },
    toolbar: {
      ids: TOOLBAR_IDS,
      search,
      onSearch: setSearch,
      searchLabel: "Buscar",
      searchPlaceholder: CARGA_FORNECEDOR_SEARCH_PLACEHOLDER,
      searchMaxLength: BUSCA_MAX_LENGTH,
      periodo: periodoMes,
      onPeriodo: escolherPeriodo,
      agrupamento: {
        value: agrupamento,
        options: AGRUPAMENTO_OPTIONS,
        hint: GROUPING_HINT,
        onChange: (value) => setAgrupamento(value as AgrupamentoCarga),
      },
      filtros: {
        definitions: filterColumns,
        value: filterValue,
        onChange: aplicarFiltros,
        ...filtrosSalvos,
      },
      perspectiva: "fornecedor",
      onPerspectiva: onPerspectivaChange,
      menu: {
        simulando,
        onEntrarSimulacao,
        onAbrirHistorico,
        canExport: status === "success" && !isEmpty,
        onExportar: exportView.open,
        onIndicadores: () => setIndicatorsOpen(true),
      },
    },
    chips: chipsDosFiltros(filterColumns, filterValue, CHIP_TEST_ID_PREFIX, rotulosDasObras),
    removerChip,
    legenda: {
      items: legendItems,
      testId: LEGEND_TEST_ID,
      simulando,
      alteracoes: cenario?.alteracoes.length ?? 0,
      simuladoPreenchido: true,
    },
    ganttFornecedor:
      mostrarGantt && agrupamento === "fornecedor"
        ? {
            rows: fornecedorRows,
            ...comAcoes,
            onResolverConflito: podeResolver ? setResolverRow : undefined,
          }
        : null,
    ganttObras:
      mostrarGantt && agrupamento === "obra"
        ? {
            rows: obraRows,
            ...comAcoes,
            onResolverConflito: podeResolver ? resolverConflitoDaObra : undefined,
          }
        : null,
    detalheDoConflito: { conflicts: conflitoSelecionado, point: pontoDoConflito },
    resolver: resolverRow
      ? {
          fornecedor: resolverRow.profissional,
          conflitos: resolverConflitos,
          escoposPorAlocacao,
          carga: cargaGantt,
          periodo: intervalo,
          today,
          canceladaVisivel: indicators.cancelada,
          onResolved: conflitoResolvido,
          onClose: () => setResolverRow(null),
          simulacao: resolucaoSimulada(resolverRow.profissional),
        }
      : null,
    exportacao: {
      aberta: exportView.isOpen,
      exportando: exportView.isExporting,
      exportar,
      fechar: exportView.close,
    },
    indicadores: {
      opcoes: INDICADORES_OBRA_E_CANCELADA,
      aberto: indicatorsOpen,
      valor: indicators,
      salvar: salvarIndicadores,
      cancelar: () => setIndicatorsOpen(false),
    },
  };
}
